import React, { createContext, useContext, useState, useEffect } from "react";
import { API_BASE } from "../constants/api";

const AuthContext = createContext(null);

// ─── Fetch override ───────────────────────────────────────────────────────────
// Runs at module import time (before any component mounts) so that every
// API call made by existing pages automatically includes the session cookie.
// localhost:3000 and localhost:5000 share the same "site" (localhost), so
// SameSite=Lax cookies are transmitted for cross-port same-site requests.
(function installCredentialsInterceptor() {
  const original = window.fetch.bind(window);
  window.fetch = (url, options) => {
    const opts = options || {};
    if (typeof url === "string" && url.startsWith(API_BASE)) {
      return original(url, { ...opts, credentials: "include" });
    }
    return original(url, opts);
  };
})();

// ─── Provider ─────────────────────────────────────────────────────────────────
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore session from cookie on page load
  useEffect(() => {
    fetch(`${API_BASE}/auth/me`, { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.success && data?.user) setUser(data.user);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password, rememberMe = false) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, rememberMe }),
      credentials: "include",
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Login failed.");
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, displayName) => {
    const res = await fetch(`${API_BASE}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, displayName }),
      credentials: "include",
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Registration failed.");
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Ignore network errors — clear local state regardless
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
